import * as React from "react";

import type { Omittable } from "../lib/props";

export type UseControllableFlagOptions = Omittable<{
  prop?: boolean;
  defaultProp?: boolean;
  onChange?: (value: boolean) => void;
}>;

/**
 * Controlled-or-uncontrolled boolean for veto-shaped state (e.g. overlay `open`).
 *
 * Unlike `useControllableState`, this does NOT keep an optimistic preview of
 * `setValue` calls in controlled mode. The parent prop is the only source of
 * truth — if the parent passes `open={false}` and `setOpen(true)` is called,
 * `onChange(true)` fires but the returned value stays `false`. This is the
 * right shape for overlays where the parent may absorb the open intent
 * (e.g. "first mobile tap reveals actions instead of opening the overlay").
 */
export function useControllableFlag({
  prop,
  defaultProp,
  onChange,
}: UseControllableFlagOptions): readonly [boolean, (next: boolean) => void] {
  const isControlled = prop !== undefined;
  const [internal, setInternal] = React.useState(defaultProp ?? false);
  const value = isControlled ? prop : internal;
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const set = React.useCallback(
    (next: boolean) => {
      if (next === valueRef.current) return;
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [isControlled, onChange],
  );
  return [value, set] as const;
}
